// Models — §9's fifth section. Founder/admin.
//
// "available models/providers; routing/assignment; local-vs-API config;
// fallbacks; usage/cost controls. Reflects Cipher's OSS/local-by-default
// gateway."
//
// L6 (this file): E9-INSTRUMENT gave every direct model-call site a trace
// write into `model_calls`, in the same row shape the real gateway already
// wrote. `backend/routers/model_calls.py` adds the one read this section
// needed — GET /api/model-calls/summary, an aggregate over that collection.
// That is real data: which models are actually being called, from which
// call sites, how often, and at what cost — over the trailing window.
//
// WHAT IS STILL NOT BUILT, AND WHY THIS SECTION SAYS SO
// =======================================================
// §9's clauses for "routing/assignment", "local-vs-API configuration" and
// "fallbacks" are still `NONE` in §4's vocabulary: nothing under `backend/`
// assigns a model to a task, exposes a local-vs-API switch, or defines a
// fallback chain as a *setting* a founder can change. E9 instruments what
// already happens; it does not add a control surface. R6 forbids drawing a
// picker that reads and writes nothing, so those three clauses stay listed
// as unbuilt rather than implied by the calls table above them.
//
// WHERE USAGE AND COST CONTROLS ACTUALLY LIVE
// ===========================================
// §9's clause for this section ends with "usage/cost controls" — the daily
// spend threshold and hard cap. Those remain on Infrastructure per the
// Founder's ruling recorded there; this section links to them rather than
// duplicating them. The per-model/per-call-site cost breakdown below is a
// different thing (what was actually spent, broken out) and belongs here.
import React from "react";
import { Link } from "react-router-dom";

import { ENDPOINTS } from "../api";
import { Block, Gate, NotBuilt, Row, Table, Value } from "../ui";
import useResource from "../useResource";

const STILL_UNBUILT = [
  ["Routing and assignment", "No endpoint assigns a model to a task."],
  ["Local-vs-API configuration", "No endpoint exposes the gateway's mode."],
  ["Fallbacks", "No endpoint defines a fallback chain as a setting."],
];

function ModelCalls() {
  const summary = useResource(ENDPOINTS.modelCallsSummary, {
    isEmpty: (d) => !d || !d.total_calls,
  });

  return (
    <Block
      title="Model calls, last 7 days"
      note="From `model_calls` — written by the gateway and by E9's direct-call tracer for the sites that bypass it."
      actions={
        <button type="button" className="btn-ghost btn-sm" onClick={summary.reload}>
          Refresh
        </button>
      }
    >
      <Gate
        resource={summary}
        empty="No model calls recorded yet in this window. E9's instrumentation is live; nothing has traced through it here yet."
      >
        {(d) => (
          <>
            <Row label="Total calls">
              <Value mono>{d.total_calls}</Value>
            </Row>
            <Row label="Total cost">
              <Value mono>{d.total_cost_usd != null ? `$${d.total_cost_usd.toFixed(4)}` : null}</Value>
            </Row>
            <Row label="Via gateway vs. direct" hint="Direct = E9's tracer, for sites not yet migrated onto the gateway.">
              <Value mono>
                {d.gateway_calls} gateway / {d.direct_calls} direct
              </Value>
            </Row>

            <div className="set-block-note dim" style={{ marginTop: "1rem" }}>
              By model
            </div>
            <Table head={["Provider", "Model", "Calls", "Cost", "Avg latency", "Errors", "Last called"]}>
              {(d.by_model || []).map((m) => (
                <tr key={`${m.provider}:${m.model}`}>
                  <td>{m.provider || "—"}</td>
                  <td className="mono">{m.model || "—"}</td>
                  <td className="mono">{m.calls}</td>
                  <td className="mono">{m.cost_usd != null ? `$${m.cost_usd.toFixed(4)}` : "—"}</td>
                  <td className="mono dim">{m.avg_latency_ms != null ? `${m.avg_latency_ms} ms` : "—"}</td>
                  <td className="mono">{m.errors}</td>
                  <td className="mono dim">{m.last_called_at || "—"}</td>
                </tr>
              ))}
            </Table>

            <div className="set-block-note dim" style={{ marginTop: "1rem" }}>
              By call site
            </div>
            <Table head={["Call site", "Path", "Calls", "Cost", "Errors", "Last called"]}>
              {(d.by_call_site || []).map((c) => (
                <tr key={`${c.call_site}:${c.instrumented_path}`}>
                  <td className="mono">{c.call_site || "—"}</td>
                  <td>
                    <span className="dim">{c.instrumented_path}</span>
                  </td>
                  <td className="mono">{c.calls}</td>
                  <td className="mono">{c.cost_usd != null ? `$${c.cost_usd.toFixed(4)}` : "—"}</td>
                  <td className="mono">{c.errors}</td>
                  <td className="mono dim">{c.last_called_at || "—"}</td>
                </tr>
              ))}
            </Table>
          </>
        )}
      </Gate>
    </Block>
  );
}

export default function Models() {
  return (
    <>
      <ModelCalls />

      <Block
        title="Still not configurable"
        note="§9 asks for these too. E9 instruments what already happens; it doesn't add a control surface for any of them."
      >
        <NotBuilt
          what="Routing, local-vs-API mode, and fallbacks are not settings a founder can change today."
          why={
            "No endpoint under backend/ assigns a model to a task, exposes the gateway's mode, or defines a " +
            "fallback chain as configuration. A picker with nothing behind it would be worse than this list " +
            "(R6), so the gap stays legible rather than implied."
          }
        />
        {STILL_UNBUILT.map(([label, status]) => (
          <Row key={label} label={label}>
            <Value>
              <span className="dim">{status}</span>
            </Value>
          </Row>
        ))}
        <Row label="Usage and cost controls" hint="Daily spend threshold and hard cap — unchanged by L6.">
          <Link className="btn-ghost btn-sm" to="/v2/settings/infrastructure">
            In Infrastructure
          </Link>
        </Row>
      </Block>
    </>
  );
}
